/**
 * Notifications domain — cache invalidation registry.
 *
 * The notifications surface has two distinct cache shapes:
 *   - `notifications` and `unread-count` (top-bar bell + dropdown)
 *   - `notifications-preferences` per-kind (settings page tables)
 *
 * `commonNotificationInvalidations()` covers the bell/dropdown set so
 * mark-as-read flows refresh both the unread count and the dropdown
 * list. The legacy `['notifications']` and `['unread-notifications']`
 * keys remain in the common set until every consumer of
 * `api.useGetNotifications` / `api.useGetUnreadNotificationsCount` is
 * migrated.
 */

import type { QueryKey } from '@tanstack/react-query'

import { type InvalidationRule } from '../invalidation/useInvalidatingMutation'
import {
  getEffectiveNotificationPreferencesByKindQueryKey,
  getUnreadNotificationsCountQueryKey,
  listNotificationsQueryKey,
} from '../generated/hooks/notificationsController'

export type NotificationsMutationId =
  | 'markAllNotificationsAsRead'
  | 'markNotificationsAsRead'
  | 'deleteNotification'
  | 'updateAllNotificationPreferencesByKind'
  | 'updateNotificationPreference'

export function commonNotificationInvalidations(): QueryKey[] {
  return [
    listNotificationsQueryKey(),
    getUnreadNotificationsCountQueryKey(),
    ['notifications'],
    ['unread-notifications'],
  ]
}

export const invalidationRegistry: Record<
  NotificationsMutationId,
  InvalidationRule<any>
> = {
  markAllNotificationsAsRead: {
    invalidates: () => commonNotificationInvalidations(),
  },
  markNotificationsAsRead: {
    invalidates: () => commonNotificationInvalidations(),
  },
  deleteNotification: {
    invalidates: () => commonNotificationInvalidations(),
  },
  updateAllNotificationPreferencesByKind: {
    invalidates: (variables: { kind_slug: string }) => [
      getEffectiveNotificationPreferencesByKindQueryKey({
        kind_slug: variables.kind_slug,
      }),
      ['notifications-preferences', variables.kind_slug],
    ],
  },
  updateNotificationPreference: {
    invalidates: (variables: { kind_slug?: string }) =>
      variables.kind_slug
        ? [
            getEffectiveNotificationPreferencesByKindQueryKey({
              kind_slug: variables.kind_slug,
            }),
            ['notifications-preferences', variables.kind_slug],
          ]
        : [],
  },
}
